import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// ✅ Base API
const BASE = "/api/usermaster";

// ================================
// GET users
// ================================
export const fetchUsersThunk = createAsyncThunk(
  "userMaster/fetchUsers",
  async (_, { rejectWithValue, signal }) => {
    try {
      const res = await api.get(BASE, { signal }); // ✅ /api/usermaster
      const list = res.data?.users || res.data?.rows || res.data || [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to load users"
      );
    }
  }
);

// ================================
// CREATE user
// ================================
export const createUserThunk = createAsyncThunk(
  "userMaster/createUser",
  async (payload, { rejectWithValue, signal }) => {
    try {
      const res = await api.post(BASE, payload, { signal }); // ✅ /api/usermaster
      if (res.data?.success === false) {
        throw new Error(res.data?.message || "Create failed");
      }
      return true;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to create user"
      );
    }
  }
);

// ================================
// UPDATE user
// ================================
export const updateUserThunk = createAsyncThunk(
  "userMaster/updateUser",
  async (payload, { rejectWithValue, signal }) => {
    try {
      const uid = payload?.uid;
      if (!uid) throw new Error("uid missing");

      const res = await api.put(`${BASE}/${uid}`, payload, { signal }); // ✅ /api/usermaster/:uid
      if (res.data?.success === false) {
        throw new Error(res.data?.message || "Update failed");
      }
      return true;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to update user"
      );
    }
  }
);

// ================================
// DELETE user
// ================================
export const deleteUserThunk = createAsyncThunk(
  "userMaster/deleteUser",
  async (uid, { rejectWithValue, signal }) => {
    try {
      const res = await api.delete(`${BASE}/${uid}`, { signal }); // ✅ /api/usermaster/:uid
      if (res.data?.success === false) {
        throw new Error(res.data?.message || "Delete failed");
      }
      return true;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to delete user"
      );
    }
  }
);

const initialState = {
  rows: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,

  filters: { role: "", status: "" },

  modalOpen: false,
  modalMode: "add",
  form: { uid: "", username: "", password: "", role: "user", status: "active" },
};

const userMasterSlice = createSlice({
  name: "userMaster",
  initialState,
  reducers: {
    setUserFilters(state, action) {
      state.filters = { ...state.filters, ...(action.payload || {}) };
    },
    openUserModal(state, action) {
      state.modalOpen = true;
      state.modalMode = action.payload?.mode || "add";
      state.error = null;
    },
    closeUserModal(state) {
      state.modalOpen = false;
      state.error = null;
    },
    setUserForm(state, action) {
      state.form = { ...state.form, ...(action.payload || {}) };
    },
    resetUserForm(state) {
      state.form = { uid: "", username: "", password: "", role: "user", status: "active" };
    },
  },
  extraReducers: (b) => {
    b
      .addCase(fetchUsersThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchUsersThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.rows = a.payload || [];
      })
      .addCase(fetchUsersThunk.rejected, (s, a) => {
        s.loading = false;
        if (a.payload === "REQUEST_CANCELLED") return;
        s.error = a.payload || "Failed to load users";
      })

      .addCase(createUserThunk.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(createUserThunk.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(createUserThunk.rejected, (s, a) => {
        s.saving = false;
        if (a.payload === "REQUEST_CANCELLED") return;
        s.error = a.payload || "Failed to create user";
      })

      .addCase(updateUserThunk.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(updateUserThunk.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(updateUserThunk.rejected, (s, a) => {
        s.saving = false;
        if (a.payload === "REQUEST_CANCELLED") return;
        s.error = a.payload || "Failed to update user";
      })

      .addCase(deleteUserThunk.pending, (s) => {
        s.deleting = true;
        s.error = null;
      })
      .addCase(deleteUserThunk.fulfilled, (s) => {
        s.deleting = false;
      })
      .addCase(deleteUserThunk.rejected, (s, a) => {
        s.deleting = false;
        if (a.payload === "REQUEST_CANCELLED") return;
        s.error = a.payload || "Failed to delete user";
      });
  },
});

export const {
  setUserFilters,
  openUserModal,
  closeUserModal,
  setUserForm,
  resetUserForm,
} = userMasterSlice.actions;

export default userMasterSlice.reducer;
